import mixpanel from "mixpanel-browser";

// ---------------------------------------------------------------------------
// User Lifecycle Stages (ordered by priority — can only promote, never demote)
// ---------------------------------------------------------------------------

export const USER_STAGES = {
  ANONYMOUS_VISITOR: "anonymous_visitor",
  ENGAGED_VISITOR: "engaged_visitor",
  INTENT_USER: "intent_user",
  IDENTIFIED_LEAD: "identified_lead",
  QUALIFIED_LEAD: "qualified_lead",
} as const;

export type UserStage = (typeof USER_STAGES)[keyof typeof USER_STAGES];

const STAGE_PRIORITY: Record<UserStage, number> = {
  [USER_STAGES.ANONYMOUS_VISITOR]: 0,
  [USER_STAGES.ENGAGED_VISITOR]: 1,
  [USER_STAGES.INTENT_USER]: 2,
  [USER_STAGES.IDENTIFIED_LEAD]: 3,
  [USER_STAGES.QUALIFIED_LEAD]: 4,
};

// ---------------------------------------------------------------------------
// Event → Stage mapping
// ---------------------------------------------------------------------------

const EVENT_STAGE_MAP: Record<string, UserStage> = {
  // Engaged Visitor: any meaningful interaction
  "Section Viewed": USER_STAGES.ENGAGED_VISITOR,
  "CTA Clicked": USER_STAGES.ENGAGED_VISITOR,
  "Nav Link Clicked": USER_STAGES.ENGAGED_VISITOR,
  "Scroll To Top Clicked": USER_STAGES.ENGAGED_VISITOR,
  "Outbound Link Clicked": USER_STAGES.ENGAGED_VISITOR,
  "Video Section Viewed": USER_STAGES.ENGAGED_VISITOR,
  "Testimonial LinkedIn Clicked": USER_STAGES.ENGAGED_VISITOR,

  // Intent User: shows purchase / membership intent
  "Meeting Modal Opened": USER_STAGES.INTENT_USER,
  "LinkedIn Auth Initiated": USER_STAGES.INTENT_USER,

  // Identified Lead: user identity is known
  "Application Submitted": USER_STAGES.IDENTIFIED_LEAD,

  // Qualified Lead: completed a high-value conversion
  "Meeting Booked": USER_STAGES.QUALIFIED_LEAD,
  "Member Application Completed": USER_STAGES.QUALIFIED_LEAD,
};

// ---------------------------------------------------------------------------
// Persistence (localStorage)
// ---------------------------------------------------------------------------

const STORAGE_KEY = "mp_user_stage";

function getPersistedStage(): UserStage {
  if (typeof window === "undefined") return USER_STAGES.ANONYMOUS_VISITOR;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored in STAGE_PRIORITY) return stored as UserStage;
  } catch {
    // localStorage may be blocked
  }
  return USER_STAGES.ANONYMOUS_VISITOR;
}

function persistStage(stage: UserStage): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, stage);
  } catch {
    // silently ignore
  }
}

// ---------------------------------------------------------------------------
// In-memory current stage
// ---------------------------------------------------------------------------

let currentStage: UserStage = USER_STAGES.ANONYMOUS_VISITOR;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise lifecycle tracking. Call once after mixpanel.init().
 * Reads the persisted stage and syncs it to Mixpanel super + people props.
 */
export function initLifecycle(): void {
  currentStage = getPersistedStage();
  syncToMixpanel(currentStage);
}

/**
 * Get the current user stage.
 */
export function getCurrentStage(): UserStage {
  return currentStage;
}

/**
 * Attempt to promote the user to `newStage`.
 * The stage only changes if `newStage` has a higher priority than the current one.
 * Returns the (possibly unchanged) current stage.
 */
export function promoteStage(newStage: UserStage): UserStage {
  if (STAGE_PRIORITY[newStage] > STAGE_PRIORITY[currentStage]) {
    currentStage = newStage;
    persistStage(currentStage);
    syncToMixpanel(currentStage);
  }
  return currentStage;
}

/**
 * Derive the appropriate stage from an event name and promote if applicable.
 * Call this inside the core `track()` wrapper so every event auto-updates the stage.
 */
export function promoteByEvent(eventName: string): UserStage {
  const targetStage = EVENT_STAGE_MAP[eventName];
  if (targetStage) {
    return promoteStage(targetStage);
  }
  return currentStage;
}

// ---------------------------------------------------------------------------
// Internal: sync stage to Mixpanel
// ---------------------------------------------------------------------------

function syncToMixpanel(stage: UserStage): void {
  try {
    // Super property — attached to every future event
    mixpanel.register({ user_stage: stage });
    // People property — visible on the user profile
    mixpanel.people.set({ user_stage: stage });
  } catch {
    // Mixpanel may not be initialised yet — safe to ignore
  }
}
